import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  BarChart, Bar, LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
} from "recharts";
import { Building2, DollarSign, Users, Car, CheckCircle2, Clock, Activity } from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { AdminPage } from "@/pages/admin/AdminLayout";
import { formatUSD } from "@/lib/format";

const RANGES = [
  { key: "7d", label: "7 days" },
  { key: "30d", label: "30 days" },
  { key: "90d", label: "90 days" },
  { key: "ytd", label: "YTD" },
];

const Kpi = ({ label, value, sub, accent = "brand", icon: Icon, testId }) => {
  const tones = {
    brand: "bg-brand text-white",
    gold: "bg-gold text-white",
    slate: "bg-slate-900 text-white",
    surface: "bg-white text-slate-900 border border-slate-200",
  };
  return (
    <div className={`rounded-2xl p-5 ${tones[accent] || tones.brand}`} data-testid={testId}>
      <div className="flex items-center gap-2 opacity-80">
        {Icon && <Icon size={13} />}
        <p className="text-[10px] uppercase tracking-widest font-semibold">{label}</p>
      </div>
      <p className={`mt-2 font-display text-3xl font-semibold ${accent === "surface" ? "text-slate-900" : ""}`}>
        {value}
      </p>
      {sub && <p className="mt-1 text-xs opacity-80">{sub}</p>}
    </div>
  );
};

const AdminDashboard = () => {
  const [range, setRange] = useState("30d");
  const [data, setData] = useState(null);

  useEffect(() => {
    setData(null);
    api.get(`/admin/analytics/overview?range=${range}`).then((r) => setData(r.data)).catch(() => setData(null));
  }, [range]);

  return (
    <AdminPage
      title="Platform overview"
      subtitle="Aggregated view across every dealership, BDC agent and lead."
      testId="admin-dashboard-page"
      action={
        <div className="inline-flex rounded-full bg-white border border-slate-200 p-1" data-testid="admin-range-toggle">
          {RANGES.map((r) => (
            <button
              key={r.key}
              onClick={() => setRange(r.key)}
              className={`px-3.5 py-1.5 rounded-full text-xs font-medium transition ${
                range === r.key ? "bg-slate-900 text-white" : "text-slate-600 hover:text-slate-900"
              }`}
              data-testid={`admin-range-${r.key}`}
            >
              {r.label}
            </button>
          ))}
        </div>
      }
    >
      {!data ? (
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
          {Array.from({ length: 5 }).map((_, i) => <div key={i} className="h-28 bg-slate-100 rounded-2xl animate-pulse" />)}
        </div>
      ) : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4" data-testid="admin-kpis-primary">
            <Kpi label="Active listings" value={data.active_listings_total.toLocaleString()} accent="brand" icon={Car} testId="admin-kpi-listings" />
            <Kpi label="Active dealers" value={data.dealers_active_count} accent="slate" icon={Building2} testId="admin-kpi-dealers" />
            <Kpi label={`Leads (${range})`} value={data.leads_total.toLocaleString()} accent="surface" icon={Activity} testId="admin-kpi-leads" />
            <Kpi label="Show rate" value={`${Math.round((data.show_rate || 0) * 100)}%`} sub={`${data.appts_showed_up}/${data.appts_showed_up + data.appts_no_show} decided`} accent="surface" icon={CheckCircle2} testId="admin-kpi-showrate" />
            <Kpi label={`Revenue billable (${range})`} value={formatUSD(data.revenue_billable, 0)} sub={`$300 × ${data.appts_showed_up} showed-up`} accent="gold" icon={DollarSign} testId="admin-kpi-revenue" />
          </div>

          {/* Phase 11 — customer explicitly asked for showed-up + no-show tiles
              front and centre and for "Sold pipeline value" to disappear. The
              show-rate lives in the primary row above; here we surface the
              raw counts + share-of-decided percentage. */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4" data-testid="admin-kpis-secondary">
            <Kpi
              label="Showed up (this month)"
              value={(data.appts_showed_up_month ?? data.appts_showed_up ?? 0).toLocaleString()}
              sub={(() => {
                const u = data.appts_showed_up_month ?? data.appts_showed_up ?? 0;
                const n = data.appts_no_show_month ?? data.appts_no_show ?? 0;
                const denom = u + n;
                if (!denom) return "no decided appts";
                return `${Math.round((u / denom) * 100)}% show rate`;
              })()}
              accent="brand"
              icon={CheckCircle2}
              testId="admin-kpi-showedup-month"
            />
            <Kpi
              label="No-show (this month)"
              value={(data.appts_no_show_month ?? data.appts_no_show ?? 0).toLocaleString()}
              sub={(() => {
                const u = data.appts_showed_up_month ?? data.appts_showed_up ?? 0;
                const n = data.appts_no_show_month ?? data.appts_no_show ?? 0;
                const denom = u + n;
                if (!denom) return "no decided appts";
                return `${Math.round((n / denom) * 100)}% no-show rate`;
              })()}
              accent="surface"
              icon={Clock}
              testId="admin-kpi-noshow-month"
            />
            <Kpi label="Customers" value={data.customers_active_count} accent="surface" icon={Users} testId="admin-kpi-customers" />
            <Kpi label="Avg lead → appt" value={data.avg_lead_to_appt_minutes ? `${(data.avg_lead_to_appt_minutes / 60).toFixed(1)}h` : "—"} accent="surface" icon={Clock} testId="admin-kpi-response-time" />
          </div>

          {/* Conversion funnel */}
          <div className="mt-8 bg-white border border-slate-200 rounded-2xl p-5" data-testid="admin-funnel">
            <p className="text-xs uppercase tracking-widest text-slate-500 font-semibold">Conversion funnel</p>
            <h3 className="mt-1 font-display text-xl font-semibold text-slate-900">From lead to sold — {range}</h3>
            <div className="mt-4 space-y-2">
              {data.conversion_funnel.map((s) => (
                <div key={s.stage} className="flex items-center gap-3">
                  <div className="w-36 text-sm font-medium text-slate-700 shrink-0">{s.stage}</div>
                  <div className="flex-1 bg-slate-100 rounded-full h-7 relative overflow-hidden">
                    <div
                      className="absolute inset-y-0 left-0 bg-brand/90 rounded-full transition-all"
                      style={{ width: `${Math.max(2, s.pct)}%` }}
                    />
                    <div className="absolute inset-0 flex items-center justify-between px-3 text-xs">
                      <span className="text-white font-semibold">{s.count.toLocaleString()}</span>
                      <span className="text-slate-600 font-medium">{s.pct.toFixed(1)}%</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Charts */}
          <div className="mt-6 grid lg:grid-cols-2 gap-4">
            <div className="bg-white border border-slate-200 rounded-2xl p-5" data-testid="admin-ts-leads">
              <p className="text-xs uppercase tracking-widest text-slate-500 font-semibold">Leads over time</p>
              <ResponsiveContainer width="100%" height={220}>
                <LineChart data={data.time_series_leads} margin={{ top: 12, right: 12, left: -12, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="date" tick={{ fontSize: 10 }} tickFormatter={(d) => d.slice(5)} />
                  <YAxis tick={{ fontSize: 10 }} allowDecimals={false} />
                  <Tooltip contentStyle={{ fontSize: 12 }} />
                  <Line type="monotone" dataKey="count" stroke="#1B3A6B" strokeWidth={2} dot={false} />
                </LineChart>
              </ResponsiveContainer>
            </div>
            <div className="bg-white border border-slate-200 rounded-2xl p-5" data-testid="admin-ts-showed">
              <p className="text-xs uppercase tracking-widest text-slate-500 font-semibold">Showed-up over time (revenue driver)</p>
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={data.time_series_showed_up} margin={{ top: 12, right: 12, left: -12, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="date" tick={{ fontSize: 10 }} tickFormatter={(d) => d.slice(5)} />
                  <YAxis tick={{ fontSize: 10 }} allowDecimals={false} />
                  <Tooltip contentStyle={{ fontSize: 12 }} />
                  <Bar dataKey="count" fill="#D4A537" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Top tables */}
          <div className="mt-6 grid lg:grid-cols-2 gap-4">
            <div className="bg-white border border-slate-200 rounded-2xl p-5" data-testid="admin-top-dealers">
              <p className="text-xs uppercase tracking-widest text-slate-500 font-semibold">Top 10 dealers by showed-up</p>
              {data.top_dealers_by_showed_up.length === 0 ? (
                <p className="mt-3 text-sm text-slate-400">No showed-up appointments yet.</p>
              ) : (
                <table className="mt-3 w-full text-sm">
                  <tbody>
                    {data.top_dealers_by_showed_up.map((d, i) => (
                      <tr key={d.dealer_id} className="border-b border-slate-100 last:border-0">
                        <td className="py-2 text-slate-400 w-6">{i + 1}.</td>
                        <td className="py-2">
                          <Link className="text-brand hover:underline font-medium" to={`/admin/dealers/${d.dealer_id}`}>
                            {d.dealer_name}
                          </Link>
                        </td>
                        <td className="py-2 text-right font-semibold">{d.count}</td>
                        <td className="py-2 text-right text-gold font-semibold">{formatUSD(d.count * 300, 0)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
            <div className="bg-white border border-slate-200 rounded-2xl p-5" data-testid="admin-top-vehicles">
              <p className="text-xs uppercase tracking-widest text-slate-500 font-semibold">Top 10 vehicles by leads</p>
              {data.top_vehicles_by_leads.length === 0 ? (
                <p className="mt-3 text-sm text-slate-400">No leads yet in this range.</p>
              ) : (
                <ul className="mt-3 space-y-2">
                  {data.top_vehicles_by_leads.map((v, i) => (
                    <li key={v.vehicle_id} className="flex items-center gap-3 py-1.5 border-b border-slate-100 last:border-0">
                      <span className="text-slate-400 w-6 text-xs">{i + 1}.</span>
                      {v.image ? (
                        <img src={v.image} alt="" className="w-12 h-9 object-cover rounded" />
                      ) : (
                        <div className="w-12 h-9 bg-slate-100 rounded" />
                      )}
                      <div className="flex-1 min-w-0">
                        <Link to={`/vehicle/${v.vehicle_id}`} className="text-sm font-medium text-slate-900 hover:text-brand truncate block">
                          {v.label}
                        </Link>
                        <p className="text-[11px] text-slate-500">{formatUSD(v.price, 0)}</p>
                      </div>
                      <span className="text-sm font-semibold text-slate-700">{v.count} lead{v.count === 1 ? "" : "s"}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </>
      )}
    </AdminPage>
  );
};

export default AdminDashboard;
